import { createContext, useContext, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  DataTable,
  Icon,
  InputText,
  SectionCard,
  Tooltip,
  Typography,
  type ColumnDef,
} from "@/deps/ui-library";
import { useNumbering, useSaveNumbering } from "@/api/queries";
import type { NumberingSequence, NumberingSettings } from "@/api/types";
import { useCan } from "@/app/session";
import { useToast } from "@/app/toasts";

/** Every number has four digits; one past 9999 grows rather than wraps (accounting ADR-0064). */
const pad = (n: number) => String(n).padStart(4, "0");

/** What comes before the digits: the prefix, a dash, and the fiscal-year code and a dash when asked for. */
function head(prefix: string, fyCode: string | null, includeFyCode: boolean) {
  return `${prefix}-${includeFyCode && fyCode ? `${fyCode}-` : ""}`;
}

/** Letters and digits, uppercased as they're typed: the dash is added, so it can't be typed. */
const cleanPrefix = (value: string) => value.toUpperCase().replace(/[^A-Z0-9]/g, "");

interface Draft {
  includeFiscalYearCode: boolean;
  rows: Record<string, { prefix: string; next: string }>;
}

function draftOf(settings: NumberingSettings): Draft {
  return {
    includeFiscalYearCode: settings.includeFiscalYearCode,
    rows: Object.fromEntries(
      settings.sequences.map((s) => [s.code, { prefix: s.prefix, next: String(s.next) }]),
    ),
  };
}

const LOCK_NOTE = {
  documents: "Locked · documents exist",
  year: "Locked after first year",
} as const;

const lockTip = (sequence: NumberingSequence) =>
  sequence.lock === "documents"
    ? `${sequence.label} already has documents, so the next number follows on from them.`
    : "The next number can only be set in the first fiscal year of using Rigo Accounting.";

/** What a row's cells read while the page edits. A context, so the columns stay one object and an input isn't remade on every keystroke. */
interface CellState {
  editing: boolean;
  fyCode: string | null;
  includeFyCode: boolean;
  prefixOf: (sequence: NumberingSequence) => string;
  nextText: (sequence: NumberingSequence) => string;
  setRow: (code: string, change: Partial<{ prefix: string; next: string }>) => void;
}

const Cells = createContext<CellState | null>(null);

function useCells(): CellState {
  const state = useContext(Cells);
  if (!state) throw new Error("A numbering cell renders inside the numbering table.");
  return state;
}

function PrefixCell({ sequence }: { sequence: NumberingSequence }) {
  const cells = useCells();
  return cells.editing ? (
    <InputText
      aria-label={`${sequence.label} prefix`}
      config={{ size: "sm" }}
      maxLength={4}
      value={cells.prefixOf(sequence)}
      onChange={(event) => cells.setRow(sequence.code, { prefix: cleanPrefix(event.target.value) })}
    />
  ) : (
    <span className="num text-foreground">{sequence.prefix}</span>
  );
}

function NextCell({ sequence }: { sequence: NumberingSequence }) {
  const cells = useCells();
  if (cells.editing && !sequence.lock) {
    return (
      <InputText
        aria-label={`${sequence.label} next number`}
        config={{ size: "sm" }}
        inputMode="numeric"
        value={cells.nextText(sequence)}
        onChange={(event) =>
          cells.setRow(sequence.code, { next: event.target.value.replace(/[^0-9]/g, "") })
        }
      />
    );
  }
  if (cells.editing && sequence.lock) {
    return (
      <Tooltip content={lockTip(sequence)}>
        <span className="flex flex-col items-end">
          <span className="num text-muted-foreground">{pad(sequence.next)}</span>
          <span className="text-muted-foreground text-xs whitespace-nowrap">
            {LOCK_NOTE[sequence.lock]}
          </span>
        </span>
      </Tooltip>
    );
  }
  return <span className="num text-foreground">{pad(sequence.next)}</span>;
}

function PreviewCell({ sequence }: { sequence: NumberingSequence }) {
  const cells = useCells();
  const h = head(cells.prefixOf(sequence), cells.fyCode, cells.includeFyCode);
  const n = Number.parseInt(cells.nextText(sequence), 10) || 1;
  return (
    <span className="num text-muted-foreground whitespace-nowrap">{`${h}${pad(n)} → ${h}${pad(n + 1)} → …`}</span>
  );
}

const COLUMNS: Array<ColumnDef<NumberingSequence>> = [
  {
    id: "type",
    header: "Document type",
    cell: ({ row: { original } }) => (
      <span className="text-foreground whitespace-nowrap">{original.label}</span>
    ),
  },
  {
    id: "prefix",
    header: "Prefix",
    size: 140,
    cell: ({ row: { original } }) => <PrefixCell sequence={original} />,
  },
  {
    id: "next",
    header: "Next number",
    size: 170,
    meta: { textAlign: "right" },
    cell: ({ row: { original } }) => <NextCell sequence={original} />,
  },
  {
    id: "preview",
    header: "Preview",
    cell: ({ row: { original } }) => <PreviewCell sequence={original} />,
  },
];

/**
 * Settings › Numbering (accounting ADR-0064; the owner's numbering design of 4 Nov 2026). A number is the prefix, a
 * dash, the fiscal-year code when the company asks for it, and four digits; every type restarts at 0001 each fiscal
 * year. One "Edit numbering" opens the options and every type's prefix; a next number opens only in the company's first
 * fiscal year, for a type with no numbered document, so a company can continue from paper. The server holds both locks.
 */
export function NumberingTab() {
  const { data: settings, isPending, error } = useNumbering();
  const save = useSaveNumbering();
  const can = useCan();
  const toast = useToast();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const editing = draft !== null;
  const mayEdit = can("settings.numbering.manage");

  if (error) {
    return (
      <Alert
        config={{ variant: "subtle", colorScheme: "danger" }}
        title="Numbering couldn't be loaded"
        description={(error as Error).message}
      />
    );
  }

  const includeFyCode = draft?.includeFiscalYearCode ?? settings?.includeFiscalYearCode ?? false;
  const fyCode = settings?.fiscalYearCode ?? null;
  const prefixOf = (s: NumberingSequence) => draft?.rows[s.code]?.prefix ?? s.prefix;
  const invoice = settings?.sequences.find((s) => s.code === "SALES");
  const example = `${head(invoice ? prefixOf(invoice) : "INV", fyCode, includeFyCode)}0248`;

  const setRow = (code: string, change: Partial<{ prefix: string; next: string }>) =>
    setDraft((d) =>
      d ? { ...d, rows: { ...d.rows, [code]: { ...d.rows[code]!, ...change } } } : d,
    );

  const submit = () => {
    if (!settings || !draft) return;
    const bad = settings.sequences.find((s) => {
      const row = draft.rows[s.code]!;
      return (
        !/^[A-Z0-9]{2,4}$/.test(row.prefix) || !/^[0-9]+$/.test(row.next) || Number(row.next) < 1
      );
    });
    if (bad) {
      setProblem(
        `${bad.label} needs a prefix of 2 to 4 letters or digits and a numeric next number.`,
      );
      return;
    }
    setProblem(null);
    save.mutate(
      {
        includeFiscalYearCode: draft.includeFiscalYearCode,
        sequences: settings.sequences.map((s) => {
          const row = draft.rows[s.code]!;
          const next = Number(row.next);
          return {
            code: s.code,
            prefix: row.prefix,
            nextNumber: s.lock || next === s.next ? null : next,
          };
        }),
      },
      {
        onSuccess: (saved) => {
          setDraft(null);
          const sales = saved.sequences.find((s) => s.code === "SALES");
          toast({
            title: `Numbering saved for ${saved.sequences.length} document types.${
              sales
                ? ` Next invoice: ${head(sales.prefix, saved.fiscalYearCode, saved.includeFiscalYearCode)}${pad(sales.next)}.`
                : ""
            }`,
            scheme: "success",
          });
        },
        onError: (failure) =>
          setProblem(failure instanceof Error ? failure.message : "Numbering couldn't be saved."),
      },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-1">
          <Typography.Heading as="h2" level="h5" weight="semibold">
            Document numbering
          </Typography.Heading>
          <Typography.Text variant="small" muted>
            Changes apply to documents created after you save.
          </Typography.Text>
        </div>
        {mayEdit && settings ? (
          editing ? (
            <span className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={save.isPending}
                onClick={() => {
                  setDraft(null);
                  setProblem(null);
                }}
              >
                Cancel
              </Button>
              <Button
                colorScheme="primary"
                size="sm"
                leftIcon="Check"
                loading={save.isPending}
                onClick={submit}
              >
                Save changes
              </Button>
            </span>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              leftIcon="Pencil"
              onClick={() => setDraft(draftOf(settings))}
            >
              Edit numbering
            </Button>
          )
        ) : null}
      </div>

      {problem ? (
        <Alert config={{ variant: "subtle", colorScheme: "danger" }} description={problem} />
      ) : null}

      <SectionCard
        title="Number format"
        note="Applies to every document type · numbers restart at 0001 each fiscal year"
      >
        <div className="flex flex-col gap-3">
          <Checkbox
            label="Include fiscal year code"
            checked={includeFyCode}
            disabled={!editing}
            onCheckedChange={(details) =>
              setDraft((d) => (d ? { ...d, includeFiscalYearCode: details.checked === true } : d))
            }
          />
          <Typography.Text variant="small" muted>
            {includeFyCode
              ? `Adds ${fyCode ?? "the fiscal year's code"} after the prefix, e.g. ${example}`
              : "Off: numbers carry no fiscal year code."}
          </Typography.Text>
          <div className="border-border flex flex-wrap items-center gap-3 border-t pt-3">
            <Typography.Text variant="small" muted>
              Example
            </Typography.Text>
            <span className="num text-foreground text-sm font-medium">{example}</span>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title="Sequences"
        note="Prefix and next number per document type · numbers are 4 digits"
        flush
      >
        <div className="border-border text-muted-foreground flex items-start gap-2 border-b px-4 py-2.5 text-xs">
          <Icon name="Info" size="xs" className="mt-0.5 shrink-0" />
          <span>
            {settings?.firstYear !== false
              ? "Next number can be set only for a document type with no documents yet, during the first fiscal year. Use it to continue numbering from paper or another system. It locks once the first document is saved or the year ends."
              : "Next numbers are locked. They can only be set during the first fiscal year of using Rigo Accounting, before a document type has its first document."}
          </span>
        </div>
        {isPending ? (
          <Typography.Text as="p" variant="small" muted className="p-4">
            Loading
          </Typography.Text>
        ) : (
          <Cells.Provider
            value={{
              editing,
              fyCode,
              includeFyCode,
              prefixOf,
              nextText: (sequence) => draft?.rows[sequence.code]?.next ?? String(sequence.next),
              setRow,
            }}
          >
            <DataTable
              aria-label="Number sequences"
              size="sm"
              enableSorting={false}
              columns={COLUMNS}
              data={settings?.sequences ?? []}
              getRowId={(sequence) => sequence.code}
              emptyContent="No document types yet."
              pagination={{ mode: "manual", show: false }}
              containerClassName="rounded-none border-0"
            />
          </Cells.Provider>
        )}
      </SectionCard>
    </div>
  );
}
